import type { ComponentProps, ReactNode, Ref } from "react";

import { cn } from "./cn";

/**
 * A native radio button with the look of the kit checkbox. Put it in a row with a label: the row, not the button, is the 44 px target
 * (32 px in the compact density).
 */
export function RadioInput({ className, ...props }: ComponentProps<"input">) {
  return (
    <input
      type="radio"
      className={cn(
        "size-5 shrink-0 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

/** A row of one radio button and its label, as tall as a target. */
export function RadioRow({ className, ...props }: ComponentProps<"label">) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the caller puts a `RadioInput` inside
    <label
      className={cn(
        "inline-flex min-h-11 cursor-pointer items-center gap-3 text-sm text-foreground compact:min-h-8",
        className,
      )}
      {...props}
    />
  );
}

/** A native radio button with its label in one grid row. The row, not the dot, is the 44 px target (32 px compact). */
export function RadioField({
  id,
  name,
  label,
  checked,
  onChange,
  inputRef,
}: Readonly<{
  id: string;
  name: string;
  label: ReactNode;
  checked: boolean;
  onChange: () => void;
  /** For a caller that moves focus to the button (for example to the first choice of a refused time). */
  inputRef?: Ref<HTMLInputElement> | undefined;
}>) {
  return (
    <div className="grid min-h-11 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8">
      <RadioInput ref={inputRef} id={id} name={name} checked={checked} onChange={onChange} />
      <label htmlFor={id} className="flex min-h-11 items-center text-sm compact:min-h-8">
        {label}
      </label>
    </div>
  );
}
